import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { getSha256, stringifyCanonicalJson } from '@/utils/hash';
import { computeEntityHashes, smartMergePayloads, type EntityHashes } from '@/lib/smartMerge';
import type { ManuscriptBackupPayload } from '@/lib/backupPayload';
import { getE2ECryptoKey } from '@/lib/cryptoKeyStore';
import { encrypt as cryptoEncrypt, decrypt as cryptoDecrypt, generateSalt, deriveKey, APP_ENCRYPTION_SALT, type EncryptedBlob } from '@/lib/crypto';
import { getE2ESetupKey, getLastSyncedKey, getLastSyncedHashKey, getEntityHashesKey } from '@/utils/storageKeys';
import type { DataRepository } from '@/types/repository';

const DEVICE_ID_KEY = 'birch_device_id';
const REVISION_KEY_PREFIX = 'deckle_last_synced_revision_';
const BASE_HASHES_KEY_PREFIX = 'deckle_last_synced_entity_hashes_';

/**
 * Returns or generates a durable UUID v4 for this client installation.
 */
export function getDeviceId(): string {
  if (typeof localStorage === 'undefined') {
    return '00000000-0000-0000-0000-000000000000';
  }

  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId || !deviceId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
    deviceId = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
}

export function getLastSyncedRevision(manuscriptId: string): number {
  if (typeof localStorage === 'undefined') return 0;
  const val = localStorage.getItem(`${REVISION_KEY_PREFIX}${manuscriptId}`);
  const parsed = val ? parseInt(val, 10) : 0;
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

export function setLastSyncedRevision(manuscriptId: string, revision: number): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(`${REVISION_KEY_PREFIX}${manuscriptId}`, revision.toString());
}

export function getLastSyncedBaseHashes(manuscriptId: string): EntityHashes | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(`${BASE_HASHES_KEY_PREFIX}${manuscriptId}`);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as EntityHashes;
  } catch {
    return null;
  }
}

export function setLastSyncedBaseHashes(manuscriptId: string, hashes: EntityHashes): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(`${BASE_HASHES_KEY_PREFIX}${manuscriptId}`, JSON.stringify(hashes));
}

export interface CommitHeadResult {
  accepted: boolean;
  currentRevision: number;
  currentPayload: string | null;
  currentPayloadHash: string | null;
  currentIsEncrypted: boolean | null;
  currentDeviceId: string | null;
  currentDeletedAt: string | null;
  currentUpdatedAt: string | null;
  fallbackUsed?: boolean;
}

/**
 * Execute Compare-and-Swap (CAS) commit of a manuscript head via RPC.
 */
export async function commitManuscriptHeadRpc(
  manuscriptId: string,
  expectedRevision: number,
  payload: string,
  payloadHash: string,
  isEncrypted: boolean,
  deviceId: string,
  deletedAt: string | null = null
): Promise<CommitHeadResult> {
  try {
    const { data, error } = await (supabase.rpc as any)('commit_manuscript_head', {
      p_manuscript_id: manuscriptId,
      p_expected_revision: expectedRevision,
      p_payload: payload,
      p_payload_hash: payloadHash,
      p_is_encrypted: isEncrypted,
      p_device_id: deviceId,
      p_deleted_at: deletedAt,
    });

    if (error) {
      logger.log('[RevisionedSync] commit_manuscript_head RPC error:', error);
      throw error;
    }

    const row = Array.isArray(data) ? data[0] : data;
    if (!row) {
      throw new Error('commit_manuscript_head returned no rows');
    }

    return {
      accepted: Boolean(row.accepted),
      currentRevision: Number(row.current_revision ?? 0),
      currentPayload: row.current_payload ?? null,
      currentPayloadHash: row.current_payload_hash ?? null,
      currentIsEncrypted: row.current_is_encrypted ?? null,
      currentDeviceId: row.current_device_id ?? null,
      currentDeletedAt: row.current_deleted_at ?? null,
      currentUpdatedAt: row.current_updated_at ?? null,
    };
  } catch (err) {
    logger.log('[RevisionedSync] RPC commit failed, checking fallback:', err);
    throw err;
  }
}

/**
 * Decrypt payload string into ManuscriptBackupPayload.
 */
export async function decryptPayloadString(
  contentStr: string,
  isEncrypted: boolean,
  userId: string
): Promise<ManuscriptBackupPayload | null> {
  try {
    let payloadText = contentStr;
    if (isEncrypted) {
      const parsed = JSON.parse(contentStr);
      if (parsed && typeof parsed === 'object' && !parsed.ciphertext && (parsed.manuscript || parsed.plannerStructure || parsed.scenes || parsed.id)) {
        return parsed as ManuscriptBackupPayload;
      }

      const encryptedBlob: EncryptedBlob = parsed;
      const masterKey = getE2ECryptoKey();
      let decrypted: string | null = null;

      if (masterKey) {
        try {
          decrypted = await cryptoDecrypt(encryptedBlob, masterKey);
        } catch {
          // Fallback to legacy key
        }
      }

      if (!decrypted) {
        try {
          const legacyKey = await deriveKey(userId, APP_ENCRYPTION_SALT);
          decrypted = await cryptoDecrypt(encryptedBlob, legacyKey);
        } catch (legacyErr) {
          console.error('[RevisionedSync] Decryption failed:', legacyErr);
        }
      }

      if (!decrypted) return null;
      payloadText = decrypted;
    }

    return JSON.parse(payloadText) as ManuscriptBackupPayload;
  } catch (err) {
    console.error('[RevisionedSync] Failed to parse/decrypt payload:', err);
    return null;
  }
}
